import React, { useState, useEffect, useRef } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { Checkbox } from 'primereact/checkbox';
import { Toast } from 'primereact/toast';
import { getUsuarios, saveUsuario, existeUserName } from '../service/UsuarioService';
import { getAdministracionesAdmin } from '../service/AdministracionService';
import { ROL_ADMINISTRADOR } from '../constants/roles';

// Pantalla del Superadmin: alta/edicion de usuarios Administrador para cada administradora.
// El rol queda fijo en Administrador; el resto de los roles los gestiona cada administradora.
const usuarioVacio = {
    usuarioId: 0,
    userName: '',
    nombre: '',
    apellido: '',
    password: '',
    email: '',
    rolId: ROL_ADMINISTRADOR,
    administracionId: null,
    activo: true,
    barriosPermitidos: [],
    personaId: null,
};

const UsuariosAdministradoras = () => {
    const toast = useRef(null);
    const [administradoras, setAdministradoras] = useState([]);
    const [administracionId, setAdministracionId] = useState(null);
    const [usuarios, setUsuarios] = useState([]);
    const [form, setForm] = useState(usuarioVacio);
    const [dialogVisible, setDialogVisible] = useState(false);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [userNameStatus, setUserNameStatus] = useState('idle'); // idle | checking | available | taken
    const userNameOriginal = useRef('');

    useEffect(() => {
        cargarAdministradoras();
    }, []);

    useEffect(() => {
        if (administracionId) cargarUsuarios(administracionId);
        else setUsuarios([]);
    }, [administracionId]);

    const cargarAdministradoras = async () => {
        try {
            const res = await getAdministracionesAdmin();
            setAdministradoras((res.data || []).map(a => ({
                label: a.activo ? a.razonSocial : `${a.razonSocial} (inactiva)`,
                value: a.administracionId,
            })));
        } catch {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'No se pudieron cargar las administradoras', life: 3000 });
        }
    };

    const cargarUsuarios = async (admId) => {
        setLoading(true);
        try {
            const res = await getUsuarios(admId);
            setUsuarios((res.data || []).filter(u => u.rolId === ROL_ADMINISTRADOR));
        } catch {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'No se pudieron cargar los usuarios', life: 3000 });
        } finally {
            setLoading(false);
        }
    };

    const abrirNuevo = () => {
        setForm({ ...usuarioVacio, administracionId });
        userNameOriginal.current = '';
        setUserNameStatus('idle');
        setDialogVisible(true);
    };

    const abrirEditar = (u) => {
        setForm({
            usuarioId:         u.usuarioId,
            userName:          u.userName,
            nombre:            u.nombre,
            apellido:          u.apellido,
            password:          '',
            email:             u.email,
            rolId:             ROL_ADMINISTRADOR,
            administracionId:  u.administracionId,
            activo:            u.activo,
            barriosPermitidos: u.barriosPermitidos || [],
            personaId:         u.personaId || null,
        });
        userNameOriginal.current = u.userName;
        setUserNameStatus('idle');
        setDialogVisible(true);
    };

    const handleChange = (field, value) => {
        setForm(prev => ({ ...prev, [field]: value }));
        if (field === 'userName') setUserNameStatus('idle');
    };

    const checkUserName = async () => {
        const userName = form.userName.trim();
        if (!userName || userName === userNameOriginal.current) {
            setUserNameStatus('idle');
            return;
        }
        setUserNameStatus('checking');
        try {
            const { data } = await existeUserName(userName, form.usuarioId || 0);
            setUserNameStatus(data.existe ? 'taken' : 'available');
        } catch {
            setUserNameStatus('idle');
        }
    };

    const guardar = async () => {
        if (!form.nombre.trim())    { toast.current.show({ severity: 'warn', summary: 'Requerido', detail: 'Ingresá el nombre', life: 3000 }); return; }
        if (!form.apellido.trim())  { toast.current.show({ severity: 'warn', summary: 'Requerido', detail: 'Ingresá el apellido', life: 3000 }); return; }
        if (!form.userName.trim())  { toast.current.show({ severity: 'warn', summary: 'Requerido', detail: 'Ingresá el usuario', life: 3000 }); return; }
        if (form.usuarioId === 0 && !form.password.trim()) {
            toast.current.show({ severity: 'warn', summary: 'Requerido', detail: 'Ingresá la contraseña', life: 3000 }); return;
        }

        const userName = form.userName.trim();
        if (userName !== userNameOriginal.current) {
            setUserNameStatus('checking');
            try {
                const { data } = await existeUserName(userName, form.usuarioId || 0);
                if (data.existe) {
                    setUserNameStatus('taken');
                    toast.current.show({ severity: 'warn', summary: 'Usuario existente', detail: 'Ese usuario (login) ya existe, elegí otro', life: 4000 });
                    return;
                }
                setUserNameStatus('available');
            } catch {
                setUserNameStatus('idle');
                toast.current.show({ severity: 'error', summary: 'Error', detail: 'No se pudo validar el usuario, intentá de nuevo', life: 3000 });
                return;
            }
        }

        setSaving(true);
        try {
            await saveUsuario({ ...form, userName, rolId: ROL_ADMINISTRADOR });
            toast.current.show({ severity: 'success', summary: 'OK', detail: 'Usuario guardado correctamente', life: 3000 });
            setDialogVisible(false);
            cargarUsuarios(administracionId);
        } catch (err) {
            const msg = err.response?.data?.errorDisplay || 'Error al guardar el usuario';
            toast.current.show({ severity: 'error', summary: 'Error', detail: msg, life: 4000 });
        } finally {
            setSaving(false);
        }
    };

    const accionesTemplate = (u) => (
        <Button
            icon="pi pi-pencil"
            className="p-button-rounded p-button-text p-button-sm"
            onClick={() => abrirEditar(u)}
            tooltip="Editar"
        />
    );

    const activoTemplate = (u) => u.activo
        ? <span className="customer-badge status-ingreso">Activo</span>
        : <span className="customer-badge status-renewal" style={{ background: '#fde8e8', color: '#c0392b' }}>No Activo</span>;

    const nombreAdministradora = administradoras.find(a => a.value === administracionId)?.label;

    const footerDialog = (
        <div className="flex justify-content-end gap-2">
            <Button label="Cancelar" icon="pi pi-times" className="p-button-text" onClick={() => setDialogVisible(false)} disabled={saving} />
            <Button label="Guardar" icon="pi pi-check" onClick={guardar} loading={saving} />
        </div>
    );

    return (
        <>
            <h4>Usuarios de Administradoras</h4>
            <br />

            <Toast ref={toast} />

            <div className="flex flex-wrap align-items-center gap-3 mb-3">
                <Dropdown
                    value={administracionId}
                    options={administradoras}
                    onChange={e => setAdministracionId(e.value)}
                    placeholder="Seleccioná una administradora"
                    filter
                    style={{ minWidth: '280px' }}
                />
                <Button label="Nuevo administrador" icon="pi pi-plus" onClick={abrirNuevo} disabled={!administracionId} />
            </div>

            <div className="grid">
                <div className="col-12">
                    <DataTable
                        value={usuarios}
                        loading={loading}
                        paginator
                        rows={10}
                        emptyMessage={administracionId ? 'Esta administradora no tiene administradores' : 'Seleccioná una administradora'}
                        responsiveLayout="scroll"
                        className="bg-white"
                    >
                        <Column field="nombre"   header="Nombre"   sortable />
                        <Column field="apellido" header="Apellido" sortable />
                        <Column field="userName" header="Usuario"  sortable />
                        <Column field="email"    header="Email"    sortable />
                        <Column header="Estado"  body={activoTemplate} style={{ width: '100px' }} />
                        <Column header=""        body={accionesTemplate} style={{ width: '60px' }} />
                    </DataTable>
                </div>
            </div>

            <Dialog
                header={form.usuarioId === 0 ? 'Nuevo administrador' : 'Editar administrador'}
                visible={dialogVisible}
                style={{ width: '480px' }}
                onHide={() => setDialogVisible(false)}
                footer={footerDialog}
            >
                <div className="p-fluid flex flex-column gap-3 mt-2">
                    <div>
                        <label className="block mb-1">Administradora</label>
                        <InputText value={nombreAdministradora || ''} disabled />
                    </div>

                    <div className="flex gap-3">
                        <div className="flex-1">
                            <label className="block mb-1">Nombre *</label>
                            <InputText value={form.nombre} onChange={e => handleChange('nombre', e.target.value)} />
                        </div>
                        <div className="flex-1">
                            <label className="block mb-1">Apellido *</label>
                            <InputText value={form.apellido} onChange={e => handleChange('apellido', e.target.value)} />
                        </div>
                    </div>

                    <div>
                        <label className="block mb-1">Usuario (login) *</label>
                        <InputText
                            value={form.userName}
                            onChange={e => handleChange('userName', e.target.value)}
                            onBlur={checkUserName}
                            className={userNameStatus === 'taken' ? 'p-invalid' : ''}
                        />
                        {userNameStatus === 'checking' && (
                            <small style={{ color: '#888' }}>Verificando disponibilidad...</small>
                        )}
                        {userNameStatus === 'available' && (
                            <small style={{ color: '#12b76a' }}>✓ Disponible</small>
                        )}
                        {userNameStatus === 'taken' && (
                            <small style={{ color: '#f04438' }}>✗ Ese usuario ya existe</small>
                        )}
                    </div>

                    <div>
                        <label className="block mb-1">Email</label>
                        <InputText value={form.email} onChange={e => handleChange('email', e.target.value)} />
                    </div>

                    <div>
                        <label className="block mb-1">
                            Contraseña {form.usuarioId > 0 && <span style={{ color: '#888', fontSize: '0.8rem' }}>(dejar vacío para no cambiar)</span>}
                            {form.usuarioId === 0 && ' *'}
                        </label>
                        <InputText
                            type="password"
                            value={form.password}
                            onChange={e => handleChange('password', e.target.value)}
                            autoComplete="new-password"
                        />
                    </div>

                    <div>
                        <label className="block mb-1">Rol</label>
                        <InputText value="Administrador" disabled />
                    </div>

                    <div className="flex align-items-center gap-2 mt-1">
                        <Checkbox
                            inputId="activo"
                            checked={form.activo}
                            onChange={e => handleChange('activo', e.checked)}
                        />
                        <label htmlFor="activo">Usuario activo</label>
                    </div>
                </div>
            </Dialog>
        </>
    );
};

export default UsuariosAdministradoras;
